import { EnvelopeIcon } from "@heroicons/react/24/solid";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { tabToName } from "@utils/utils";
import { isBefore, isFuture } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";

import { Badge, Small, TabNavigation } from "@fronterahealth/frontera-ui-components";

import { useLearnerByIdQuery } from "@api/graphql/types-and-hooks";
import { StageType } from "@components/ClientTable/ClientTable";
import { PageHeader, PageHeaderTabsContainer } from "@components/PageHeader/PageHeader";
import "@components/notifications/notifications.css";
import { useAdminData } from "@providers/AdminDataProvider";

const clientDetailsNavigationTabs = [
  "basic-info",
  "programming",
  "ehr-data",
  "intake-details",
  "file-library",
  "caregivers",
  "providers",
];

// Tabs whose own sub-tabs add a path segment (clients/:clientId/<tab>/<sub-tab>).
const TABS_WITH_SUB_TABS = new Set(["intake-details", "ehr-data"]);

const tabDisplayName: Record<string, string> = {
  "basic-info": "Overview",
  programming: "Programming (BETA)",
  "ehr-data": "EHR Data (Internal)",
  "intake-details": "Intake Details",
  "file-library": "File Library",
  caregivers: "Caregivers",
  providers: "Providers",
};

export const getStage = (
  hasPreviousDiagnosis: boolean,
  diagnosisDate: Date | null,
  assessmentDate: Date | null,
): StageType => {
  const currentDate = new Date();

  if (!hasPreviousDiagnosis && !diagnosisDate) {
    return "Diagnosis Waitlist"; // No previous diagnosis and no diagnosis date, client is on the diagnosis waitlist
  }

  if (!hasPreviousDiagnosis && diagnosisDate && isFuture(diagnosisDate)) {
    return "Diagnosis Pending"; // Diagnosis date is in the future and no previous diagnosis
  }

  if ((hasPreviousDiagnosis && !assessmentDate) || (diagnosisDate && isBefore(diagnosisDate, currentDate))) {
    return "Assessment Waitlist"; // Previous diagnosis exists but no assessment date, or diagnosis date has passed
  }

  if (hasPreviousDiagnosis && assessmentDate && isFuture(assessmentDate)) {
    return "Assessment Pending"; // Previous diagnosis exists and Assessment date is in the future
  }

  if (hasPreviousDiagnosis && assessmentDate && isBefore(assessmentDate, currentDate)) {
    return "Assessment Complete"; // Previous diagnosis exists and Assessment date has passed
  }

  return "Diagnosis Waitlist"; // Default to diagnosis waitlist
};

export const ClientDetails: React.FC = () => {
  const { pathname, state } = useLocation();
  const { isOnboardingEnabled, roles } = useAdminData();
  const [clientName, setClientName] = useState<string>("");
  const flags = useFlags<FeatureFlags>();

  const { clientId } = useParams();

  const learnerDetailsQuery = useLearnerByIdQuery(
    { learnerId: clientId ? clientId : "" },
    {
      queryKey: ["learner-by-id", clientId],
      enabled: !!clientId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const { data } = learnerDetailsQuery;
  const invitationStatus =
    data?.learnerById?.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.invitationStatus;
  const invitationExpiresAt =
    data?.learnerById?.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.invitationExpiresAt;
  const invitationCreatedAt =
    data?.learnerById?.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.invitationCreatedAt;

  const invitationHeading = () => {
    if (invitationStatus === "PENDING") {
      return `Caregiver Invited on ${new Date(invitationCreatedAt).toLocaleDateString()}`;
    } else if (invitationStatus === "EXPIRED") {
      return `Caregiver Invite Expired on ${new Date(invitationExpiresAt).toLocaleDateString()}`;
    } else if (invitationStatus === "ACCEPTED") {
      return "Caregiver Invite Accepted";
    } else {
      return "Caregiver Not Invited";
    }
  };

  useEffect(() => {
    if (data && data.learnerById) {
      setClientName(`${data.learnerById?.learnermetadata?.firstName} ${data.learnerById?.learnermetadata?.lastName}`);
    }
  }, [data]);

  let navigationTabs = clientDetailsNavigationTabs;

  if (!isOnboardingEnabled) {
    navigationTabs = navigationTabs.filter((item) => item !== "intake-details");
  }

  if (!flags["treatment-plan-upload"] && !flags["ia-to-programming"]) {
    navigationTabs = navigationTabs.filter((item) => item !== "programming");
  }

  if (!roles.includes("frontera-internal")) {
    navigationTabs = navigationTabs.filter((item) => item !== "ehr-data");
  }

  const pathSegments = pathname.split("/").filter(Boolean);
  const lastSegment =
    pathSegments.length > 3 && TABS_WITH_SUB_TABS.has(pathSegments[2])
      ? pathSegments[2] // clients/:clientId/<tab>/<sub-tab>/...: the tab is the third segment
      : pathSegments[pathSegments.length - 1];

  return (
    <div className="flex h-full w-full flex-col">
      <PageHeader
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Clients", href: "/clients" },
          { label: clientName || "Client" },
        ]}
        title={<span className="ph-no-capture">{clientName}</span>}
        meta={
          <>
            {data?.learnerById?.archived && <Badge appearance="neutral" text="Archived" />}
            <div className="flex items-center rounded-md border border-limestone-100 px-2 py-1">
              <EnvelopeIcon className="mr-1 h-4 w-4 text-text-secondary" />
              <Small>{invitationHeading()}</Small>
            </div>
          </>
        }
      />
      <PageHeaderTabsContainer>
        <TabNavigation
          tabs={navigationTabs.map((tab) => ({
            current: lastSegment === tab,
            type: "component",
            name: tab,
            Component: (
              <Link to={tab} state={state}>
                {tabDisplayName[tab] ?? tabToName(tab)}
              </Link>
            ),
          }))}
        />
      </PageHeaderTabsContainer>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
};
